import { createContext, useCallback, useContext, useState, useEffect } from "react";
import { fetchTours } from "../common/api";
import { useTourSSE } from "../hook/useTourSSE";

const TourContext = createContext();

function getLocalDateString() {
	const d = new Date();
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

export function TourProvider({ children }) {
	const [tours, setTours] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [refreshKey, setRefreshKey] = useState(0);
	const [selectedDate, setSelectedDate] = useState(getLocalDateString());

	useEffect(() => {
		setLoading(true);
		setError(null);
		fetchTours(selectedDate)
			.then(setTours)
			.catch((err) => {
				console.error(err);
				setError("We couldn't load the tours. Please check your connection and try again.");
			})
			.finally(() => setLoading(false));
	}, [selectedDate, refreshKey]);

	const retryTours = () => setRefreshKey((key) => key + 1);

	const handleAvailabilityUpdate = useCallback((tourId, date, count) => {
		if (date && String(date).slice(0, 10) !== selectedDate) return;
		const participantCount = Number(count);
		if (!Number.isFinite(participantCount)) return;

		setTours((prev) =>
			prev.map((t) =>
				String(t.id) === String(tourId)
					? { ...t, participant_count: participantCount }
					: t
			)
		);
	}, [selectedDate]);

	const liveStatus = useTourSSE(handleAvailabilityUpdate);

	const addParticipant = (tourId, count) => {
		setTours((prev) =>
			prev.map((t) =>
				t.id === tourId
					? { ...t, participant_count: Number(t.participant_count) + count }
					: t
			)
		);
	};

	const getParticipants = (tourId) =>
		tours.find((t) => t.id === tourId)?.participant_count ?? 0;

	return (
		<TourContext.Provider value={{ tours, loading, error, retryTours, liveStatus, selectedDate, setSelectedDate, addParticipant, getParticipants }}>
			{children}
		</TourContext.Provider>
	);
}

export const useTour = () => useContext(TourContext);
